<script setup lang="ts">
import { observeModalAutoSize } from './auto-size';
import type { UiGap } from '../types';
import { modalContextKey, unregisterModal } from './stack';
import { gapClasses, paddingClasses, type UiJustification, type UiPadding } from '../ui-layout/classes';

defineOptions({
    name: 'IdeoModal',
    inheritAttrs: false
});

type ModalLayer = 'default' | 'nested';
type ModalViewport = 'default' | 'inset' | 'workspace';
type ModalContentLayout = 'default' | 'fill';
type ModalBodyLayout = 'default' | 'stack' | 'grid';
type ModalBodyOverflow = 'auto' | 'hidden' | 'visible';
type ModalHeaderDensity = 'compact' | 'default' | 'spacious';
type ModalFooterLayout = 'standard' | 'content';

const openModel = defineModel<boolean>('open', { default: false });
const props = withDefaults(defineProps<{
    id?: string;
    title?: string;
    fullscreen?: boolean;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
    width?: number | string | null;
    height?: number | string | null;
    minHeight?: number | string | null;
    maxHeight?: number | string | null;
    hideHeader?: boolean;
    hideFooter?: boolean;
    hideClose?: boolean;
    keepRendered?: boolean;
    layer?: ModalLayer;
    viewport?: ModalViewport;
    contentLayout?: ModalContentLayout;
    bodyLayout?: ModalBodyLayout;
    bodyPadding?: UiPadding;
    bodyGap?: UiGap;
    bodyOverflow?: ModalBodyOverflow;
    bodyGutter?: 'auto' | 'stable';
    bodyHeight?: 'auto' | 'fill' | 'viewport';
    bodyFlow?: boolean;
    headerDensity?: ModalHeaderDensity;
    footerPadding?: UiPadding;
    footerJustify?: UiJustification;
    footerLayout?: ModalFooterLayout;
    closeOnBackdrop?: boolean;
    closeOnEscape?: boolean;
    static?: boolean;
    elevated?: boolean;
    hidden?: boolean;
}>(), {
    title: null,
    size: 'md',
    width: null,
    height: null,
    minHeight: null,
    maxHeight: null,
    layer: 'default',
    viewport: 'default',
    contentLayout: 'default',
    bodyLayout: 'default',
    bodyPadding: 'md',
    bodyGap: 'sm',
    bodyOverflow: 'auto',
    bodyGutter: 'stable',
    bodyHeight: 'auto',
    bodyFlow: false,
    headerDensity: 'default',
    footerPadding: 'md',
    footerJustify: 'end',
    footerLayout: 'standard',
    closeOnBackdrop: true,
    closeOnEscape: true,
    static: false,
    elevated: false,
    hidden: false
});

const emit = defineEmits<{
    (e: 'ok'): void;
    (e: 'close'): void;
    (e: 'hidden'): void;
}>();

const attrs = useAttrs();
const slots = useSlots();
const instance = getCurrentInstance();
const generatedId = useId();
const dialogRef = ref<HTMLDialogElement | null>(null);
const contentRef = ref<HTMLElement | null>(null);
const dialog = useDialog(dialogRef);
const isRendered = ref(false);
const wasRendered = ref(false);
const parentModal = inject(modalContextKey, null);

provide(modalContextKey, {
    dialog: dialogRef,
    active: computed(() => isRendered.value && !props.hidden && (parentModal?.active.value ?? true))
});

const visible = ref(false);
const okReturnValue = 'ok';
const closeReturnValue = 'close';
const closeAnimationFallbackDelay = 240;
const backdropCloseDistance = 300;

let closeAnimationTimeout: number | null = null;
let closeAnimationCleanup: (() => void) | null = null;
let firstFrame: number | null = null;
let secondFrame: number | null = null;
let syncingModel = false;
let autoSize: ReturnType<typeof observeModalAutoSize> | null = null;

const dialogId = computed(() => props.id ?? `ideo-modal-${generatedId}`);
const titleId = computed(() => `${dialogId.value}-title`);
const largeViewport = computed(() => props.fullscreen || props.viewport !== 'default');
const showHeader = computed(() => !largeViewport.value && !props.hideHeader && (Boolean(props.title) || Boolean(slots['modal-header'])));
const hasOkListener = computed(() => Boolean(instance?.vnode.props?.onOk));
const showFooter = computed(() => !largeViewport.value && !props.hideFooter && (
    Boolean(slots['modal-footer'])
    || Boolean(slots['btn-close'])
    || Boolean(slots['btn-submit'])
    || hasOkListener.value
));
const shouldRenderContent = computed(() => isRendered.value || (props.keepRendered && wasRendered.value));

const sizeWidths: Record<NonNullable<typeof props.size>, string> = {
    sm: '380px',
    md: '560px',
    lg: '800px',
    xl: '1040px',
    xxl: '1400px'
};

const headerDensityClasses: Record<ModalHeaderDensity, string> = {
    compact: 'min-h-[44px] gap-[10px] px-[12px] py-[8px]',
    default: 'min-h-[52px] gap-[10px] px-[16px] py-[12px]',
    spacious: 'min-h-[60px] gap-[10px] px-[20px] py-[16px]'
};

const headerTitleClasses: Record<ModalHeaderDensity, string> = {
    compact: 'text-[0.8125rem]/[1.25]',
    default: 'text-[0.9375rem]/[1.25]',
    spacious: 'text-[0.9375rem]/[1.25]'
};

const footerPaddingClasses: Record<UiPadding, string> = {
    none: 'p-0',
    xs: 'px-[4px] py-[4px]',
    sm: 'px-[8px] py-[8px]',
    md: 'px-[16px] py-[10px]',
    lg: 'px-[20px] py-[12px]'
};

const bodyPaddingClasses: Record<UiPadding, string> = {
    none: paddingClasses.none,
    xs: paddingClasses.xs,
    sm: paddingClasses.sm,
    md: 'px-[16px] py-[12px]',
    lg: paddingClasses.lg
};

const bodyLayoutClasses: Record<ModalBodyLayout, string> = {
    default: '',
    stack: 'flex flex-col',
    grid: 'grid'
};

const bodyOverflowClasses: Record<ModalBodyOverflow, string> = {
    auto: 'overflow-x-hidden overflow-y-auto',
    hidden: 'overflow-hidden',
    visible: 'overflow-visible'
};

const bodyHeightClasses: Record<'auto' | 'fill' | 'viewport', string> = {
    auto: 'flex-[0_1_auto]',
    fill: 'flex-1',
    viewport: 'h-[calc(100dvh-56px)] flex-none'
};

const effectiveBodyLayout = computed<ModalBodyLayout>(() => props.bodyFlow ? 'stack' : props.bodyLayout);
const effectiveBodyOverflow = computed<ModalBodyOverflow>(() => props.bodyFlow ? 'hidden' : props.bodyOverflow);
const effectiveBodyHeight = computed<'auto' | 'fill' | 'viewport'>(() => props.bodyFlow || largeViewport.value ? 'fill' : props.bodyHeight);

const autoHeightEnabled = computed(() => !props.fullscreen
    && props.viewport === 'default'
    && (props.height === null || props.height === undefined || props.height === 'auto')
    && props.contentLayout === 'default'
    && effectiveBodyHeight.value === 'auto'
);

const autoWidthEnabled = computed(() => !largeViewport.value && props.width === 'auto');
const autoSizeTransitionClass = computed(() =>
{
    if (autoHeightEnabled.value && autoWidthEnabled.value)
        return 'motion-safe:[transition-property:opacity,scale,max-height]';

    if (autoHeightEnabled.value)
        return 'motion-safe:[transition-property:opacity,scale,width,max-height]';

    if (autoWidthEnabled.value)
        return 'motion-safe:[transition-property:opacity,scale,height,max-height]';

    return '';
});

const bodyClasses = computed(() => [
    'ideo-modal-body min-h-0 [&:has(.picker-open)]:overflow-visible',
    bodyPaddingClasses[largeViewport.value ? 'none' : props.bodyPadding],
    bodyLayoutClasses[effectiveBodyLayout.value],
    effectiveBodyLayout.value === 'default' ? '' : gapClasses[props.bodyGap],
    bodyOverflowClasses[effectiveBodyOverflow.value],
    effectiveBodyOverflow.value === 'auto' ? 'data-[modal-auto-resizing]:overflow-y-hidden' : '',
    props.bodyGutter === 'stable' && effectiveBodyOverflow.value === 'auto' ? '[scrollbar-gutter:stable]' : '',
    bodyHeightClasses[effectiveBodyHeight.value]
]);

const contentClasses = computed(() => [
    'content ideo-modal-content flex max-h-[inherit] flex-col overflow-hidden rounded-[inherit] bg-surface dark:bg-surface-dark [&:has(.picker-open)]:overflow-visible',
    largeViewport.value || props.contentLayout === 'fill' ? 'h-full min-h-full' : '',
    autoHeightEnabled.value ? 'h-[var(--ideo-modal-auto-height,auto)]' : ''
]);

const dialogClasses = computed(() => [
    'dialog ideo-modal-dialog modal-overlay modal-card m-auto overflow-visible rounded-[8px] border border-transparent bg-surface p-0 text-fg shadow-none backdrop:bg-overlay backdrop:[transition:opacity_0.22s_ease,backdrop-filter_0.22s_ease] motion-reduce:transition-none dark:border-[color-mix(in_oklch,var(--color-border-strong-dark)_75%,black)] dark:bg-surface-dark dark:text-fg-dark dark:backdrop:bg-overlay-dark [&:focus:not(:focus-visible)]:outline-none [&:focus-visible]:outline-none',
    largeViewport.value ? '[transition:opacity_0.22s_ease]' : '[transition:opacity_0.2s_ease,scale_0.22s_cubic-bezier(.2,.8,.3,1.05),width_0.26s_cubic-bezier(.32,.72,0,1),height_0.26s_cubic-bezier(.32,.72,0,1),max-height_0.26s_cubic-bezier(.32,.72,0,1)]',
    autoHeightEnabled.value ? '[--ideo-modal-auto-height:initial] h-[var(--ideo-modal-auto-height,fit-content)]' : '',
    autoWidthEnabled.value ? '[--ideo-modal-auto-width:initial]' : '',
    autoSizeTransitionClass.value,
    visible.value
        ? 'opacity-100 backdrop:opacity-100 backdrop:backdrop-blur-[4px]'
        : 'opacity-0 backdrop:opacity-0 backdrop:backdrop-blur-[0px]',
    !largeViewport.value ? (visible.value ? 'scale-100' : 'scale-[0.94]') : '',
    largeViewport.value
        ? '!m-0 !h-dvh !min-h-0 !max-h-dvh !w-dvw !max-w-none !rounded-none !border-0'
        : '',
    props.layer === 'nested' ? 'nested' : '',
    props.hidden ? 'pointer-events-none invisible' : ''
]);

function cssSize(value: number | string | null | undefined)
{
    if (typeof value === 'number')
        return `${value}px`;

    return value || undefined;
}

const dialogStyle = computed(() =>
{
    if (props.fullscreen || props.viewport !== 'default')
        return null;

    const width = cssSize(props.width) ?? sizeWidths[props.size];

    return {
        width: width === 'auto' ? 'var(--ideo-modal-auto-width, fit-content)' : `min(${width}, 94vw)`,
        maxWidth: '94vw',
        height: autoHeightEnabled.value ? undefined : cssSize(props.height),
        minHeight: cssSize(props.minHeight),
        maxHeight: cssSize(props.maxHeight) ?? '88dvh'
    };
});

function stopAutoSize()
{
    autoSize?.stop();
    autoSize = null;
}

function startAutoSize()
{
    stopAutoSize();

    if ((autoHeightEnabled.value || autoWidthEnabled.value) && !props.hidden && dialogRef.value?.open && contentRef.value)
        autoSize = observeModalAutoSize(dialogRef.value, contentRef.value, { height: autoHeightEnabled.value, width: autoWidthEnabled.value });
}

function reducedMotion()
{
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function syncModel(value: boolean)
{
    syncingModel = true;
    openModel.value = value;
    nextTick(() =>
    {
        syncingModel = false;
    });
}

function clearFrames()
{
    if (firstFrame !== null)
        cancelAnimationFrame(firstFrame);

    if (secondFrame !== null)
        cancelAnimationFrame(secondFrame);

    firstFrame = null;
    secondFrame = null;
}

function clearCloseAnimation()
{
    if (closeAnimationTimeout !== null)
    {
        window.clearTimeout(closeAnimationTimeout);
        closeAnimationTimeout = null;
    }

    closeAnimationCleanup?.();
    closeAnimationCleanup = null;
}

function scheduleVisible()
{
    clearFrames();

    if (reducedMotion())
    {
        visible.value = true;

        return;
    }

    firstFrame = requestAnimationFrame(() =>
    {
        secondFrame = requestAnimationFrame(() =>
        {
            visible.value = true;
            firstFrame = null;
            secondFrame = null;
        });
    });
}

function emitHidden()
{
    nextTick(() => emit('hidden'));
}

function finishNativeClose(returnValue: string)
{
    clearCloseAnimation();

    if (!dialogRef.value?.open)
        return;

    dialogRef.value.returnValue = returnValue;
    dialogRef.value.close(returnValue);
}

function requestClose(returnValue = closeReturnValue)
{
    const currentDialog = dialogRef.value;

    if (!currentDialog?.open)
        return;

    clearFrames();
    clearCloseAnimation();
    visible.value = false;

    if (reducedMotion())
    {
        finishNativeClose(returnValue);

        return;
    }

    const closingProperty = largeViewport.value ? 'opacity' : 'scale';

    function onTransitionEnd(event: TransitionEvent)
    {
        if (event.target === currentDialog && event.propertyName === closingProperty)
            finishNativeClose(returnValue);
    }

    currentDialog.addEventListener('transitionend', onTransitionEnd);
    closeAnimationCleanup = () => currentDialog.removeEventListener('transitionend', onTransitionEnd);
    closeAnimationTimeout = window.setTimeout(() => finishNativeClose(returnValue), closeAnimationFallbackDelay);
}

function onCloseAction()
{
    requestClose(closeReturnValue);
}

function onOkAction()
{
    requestClose(okReturnValue);
}

function onNativeOpen()
{
    clearCloseAnimation();
    isRendered.value = true;
    visible.value = false;
    syncModel(true);
    scheduleVisible();
}

function onNativeOpened()
{
    wasRendered.value = true;
    startAutoSize();
}

function onNativeOpenCancelled()
{
    if (dialogRef.value?.open)
        return;

    clearFrames();
    clearCloseAnimation();
    stopAutoSize();
    visible.value = false;
    isRendered.value = false;
    syncModel(false);
}

function onNativeClose(event: Event)
{
    const currentDialog = event.currentTarget as HTMLDialogElement;

    if (currentDialog.open)
        return;

    const returnValue = currentDialog.returnValue;

    clearFrames();
    clearCloseAnimation();
    stopAutoSize();
    visible.value = false;
    isRendered.value = false;
    syncModel(false);

    if (returnValue === okReturnValue)
        emit('ok');
    else if (returnValue === closeReturnValue)
        emit('close');

    currentDialog.returnValue = '';
    emitHidden();
}

function onRequestClose(event: Event)
{
    const request = event as CustomEvent<{ returnValue?: string }>;

    request.preventDefault();
    requestClose(request.detail?.returnValue ?? '');
}

function onCancel(event: Event)
{
    event.preventDefault();

    if (!props.static && props.closeOnEscape)
        onCloseAction();
}

function onDialogClick(event: MouseEvent)
{
    if (event.target === dialogRef.value && !props.static && props.closeOnBackdrop && !withinBackdropGuard(event))
        onCloseAction();
}

function withinBackdropGuard(event: MouseEvent)
{
    const content = contentRef.value;

    if (!content)
        return true;

    const rect = content.getBoundingClientRect();
    const horizontalDistance = Math.max(rect.left - event.clientX, 0, event.clientX - rect.right);
    const verticalDistance = Math.max(rect.top - event.clientY, 0, event.clientY - rect.bottom);

    return Math.hypot(horizontalDistance, verticalDistance) <= backdropCloseDistance;
}

watch(openModel, async (value) =>
{
    if (syncingModel)
        return;

    if (value)
        await dialog.open();
    else
        requestClose('');
});

watch([autoHeightEnabled, autoWidthEnabled, () => props.hidden], () =>
{
    stopAutoSize();
    nextTick(startAutoSize);
});

watch(dialogStyle, () => nextTick(() => autoSize?.refresh()));

onMounted(() =>
{
    if (openModel.value)
        void dialog.open();
});

onBeforeUnmount(() =>
{
    stopAutoSize();
    clearFrames();
    clearCloseAnimation();

    if (dialogRef.value)
        unregisterModal(dialogRef.value);

    if (dialogRef.value?.open)
    {
        dialogRef.value.returnValue = '';
        dialogRef.value.close();
    }
});

defineExpose(dialog);
</script>

<template>
    <dialog
        :id="dialogId"
        ref="dialogRef"
        v-bind="attrs"
        :class="dialogClasses"
        :style="dialogStyle"
        :aria-label="(attrs['aria-label'] as string | undefined) ?? (largeViewport ? props.title : undefined)"
        :aria-labelledby="showHeader && props.title ? titleId : undefined"
        :data-modal-size="props.size"
        :data-modal-visible="visible"
        data-modal-enter-duration="220"
        data-modal-leave-duration="220"
        :data-modal-card-opacity-duration="largeViewport ? 220 : 200"
        :data-modal-card-easing="largeViewport ? 'ease' : 'cubic-bezier(.2,.8,.3,1.05)'"
        data-modal-geometry-duration="260"
        @ideo-modal-open="onNativeOpen"
        @ideo-modal-opened="onNativeOpened"
        @ideo-modal-open-cancelled="onNativeOpenCancelled"
        @ideo-modal-request-close="onRequestClose"
        @cancel="onCancel"
        @close="onNativeClose"
        @click="onDialogClick"
    >
        <div v-if="shouldRenderContent" ref="contentRef" :class="contentClasses" @click.stop>
            <slot v-if="largeViewport && !props.hideHeader" name="modal-header" :ok="onOkAction" :cancel="onCloseAction" />
            <header v-if="showHeader" :class="['relative flex shrink-0 items-center border-b border-border dark:border-border-dark', headerDensityClasses[props.headerDensity]]" data-modal-header>
                <span v-if="slots['modal-header'] && props.title" :id="titleId" class="sr-only">{{ props.title }}</span>
                <slot name="modal-header">
                    <h2 :id="titleId" :class="['min-w-0 flex-1 truncate font-[600] text-fg dark:text-fg-dark', headerTitleClasses[props.headerDensity]]">
                        {{ props.title }}
                    </h2>
                </slot>
                <IdeoAction
                    v-if="!props.hideClose"
                    type="button"
                    variant="secondary"
                    icon="x"
                    class="close ms-auto shrink-0"
                    :aria-label="$t('[[[Zamknij]]]')"
                    :title="$t('[[[Zamknij]]]')"
                    data-modal-close
                    @click.stop="onCloseAction"
                />
            </header>

            <main :class="bodyClasses" data-modal-body>
                <slot :ok="onOkAction" :cancel="onCloseAction" />
            </main>

            <footer
                v-if="showFooter"
                :class="['relative flex min-h-[52px] shrink-0 items-center border-t border-border dark:border-border-dark', footerPaddingClasses[props.footerPadding]]"
                data-modal-footer
            >
                <IdeoActionGroup v-if="props.footerLayout === 'standard'" class="w-full flex-wrap" :justify="props.footerJustify">
                    <slot name="modal-footer" :ok="onOkAction" :cancel="onCloseAction">
                        <IdeoAction variant="secondary" @click.stop="onCloseAction">
                            <IdeoIcon name="ban" :size="14" aria-hidden="true" />
                            <slot name="btn-close">{{ $t('[[[Zamknij]]]') }}</slot>
                        </IdeoAction>
                        <IdeoAction variant="primary" @click.stop="onOkAction">
                            <IdeoIcon name="check" :size="14" aria-hidden="true" />
                            <slot name="btn-submit">{{ $t('[[[Ok]]]') }}</slot>
                        </IdeoAction>
                    </slot>
                </IdeoActionGroup>
                <div v-else class="w-full">
                    <slot name="modal-footer" :ok="onOkAction" :cancel="onCloseAction" />
                </div>
            </footer>
            <slot v-if="largeViewport && !props.hideFooter" name="modal-footer" :ok="onOkAction" :cancel="onCloseAction" />
        </div>

        <div class="teleport-target contents" data-ideo-modal-teleport-target />
    </dialog>
</template>
